import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { packagesAPI } from '../../utils/api';

export const PackagesManager = () => {
  const { toast } = useToast();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newPackage, setNewPackage] = useState({
    name: '',
    price: '',
    duration: '',
    features: [''],
    popular: false,
    active: true
  });

  const fetchPackages = useCallback(async () => {
    try {
      const response = await packagesAPI.getAllAdmin();
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast({
        title: "Error",
        description: "Failed to load advertising packages.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleEdit = (pkg) => {
    setEditingId(pkg.id);
    setEditForm({ ...pkg });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    try {
      await packagesAPI.update(editingId, editForm);
      toast({
        title: "Package Updated",
        description: "Advertising package has been successfully updated.",
      });
      setEditingId(null);
      setEditForm({});
      fetchPackages();
    } catch (error) {
      console.error('Error updating package:', error);
      toast({
        title: "Error",
        description: "Failed to update package.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        await packagesAPI.delete(id);
        toast({
          title: "Package Deleted",
          description: "Advertising package has been removed.",
        });
        fetchPackages();
      } catch (error) {
        console.error('Error deleting package:', error);
        toast({
          title: "Error",
          description: "Failed to delete package.",
          variant: "destructive"
        });
      }
    }
  };

  const handleAddPackage = async () => {
    try {
      const packageToAdd = {
        ...newPackage,
        price: parseFloat(newPackage.price) || 0,
        features: newPackage.features.filter(f => f.trim() !== '')
      };
      await packagesAPI.create(packageToAdd);
      toast({
        title: "Package Added",
        description: "New advertising package has been created.",
      });
      setIsAdding(false);
      setNewPackage({
        name: '',
        price: '',
        duration: '',
        features: [''],
        popular: false,
        active: true
      });
      fetchPackages();
    } catch (error) {
      console.error('Error creating package:', error);
      toast({
        title: "Error",
        description: "Failed to create package.",
        variant: "destructive"
      });
    }
  };

  const handleFeatureChange = (index, value, isEdit = false) => {
    if (isEdit) {
      const updatedFeatures = [...editForm.features];
      updatedFeatures[index] = value;
      setEditForm({ ...editForm, features: updatedFeatures });
    } else {
      const updatedFeatures = [...newPackage.features];
      updatedFeatures[index] = value;
      setNewPackage({ ...newPackage, features: updatedFeatures });
    }
  };

  const addFeatureField = (isEdit = false) => {
    if (isEdit) {
      setEditForm({ ...editForm, features: [...editForm.features, ''] });
    } else {
      setNewPackage({ ...newPackage, features: [...newPackage.features, ''] });
    }
  };

  const removeFeatureField = (index, isEdit = false) => {
    if (isEdit) {
      const updatedFeatures = editForm.features.filter((_, i) => i !== index);
      setEditForm({ ...editForm, features: updatedFeatures });
    } else {
      const updatedFeatures = newPackage.features.filter((_, i) => i !== index);
      setNewPackage({ ...newPackage, features: updatedFeatures });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading packages...</div>;
  }

  return (
    <div className="space-y-6" data-testid="packages-manager">
      {/* Add New Package Button */}
      {!isAdding && (
        <div>
          <Button
            onClick={() => setIsAdding(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="add-package-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Package
          </Button>
        </div>
      )}

      {/* Add New Package Form */}
      {isAdding && (
        <Card className="border-2 border-emerald-300">
          <CardHeader className="bg-emerald-50">
            <CardTitle>Add New Advertising Package</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Package Name *
                  </label>
                  <input
                    type="text"
                    value={newPackage.name}
                    onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                    placeholder="e.g., Featured Partner"
                    data-testid="package-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Price (USD) *
                  </label>
                  <input
                    type="number"
                    value={newPackage.price}
                    onChange={(e) => setNewPackage({ ...newPackage, price: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                    placeholder="e.g., 499"
                    data-testid="package-price-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Duration *
                </label>
                <input
                  type="text"
                  value={newPackage.duration}
                  onChange={(e) => setNewPackage({ ...newPackage, duration: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                  placeholder="e.g., 3 months"
                  data-testid="package-duration-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Features
                </label>
                {newPackage.features.map((feature, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value, false)}
                      className="flex-1 px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                      placeholder="e.g., Homepage banner placement"
                      data-testid={`feature-input-${index}`}
                    />
                    {newPackage.features.length > 1 && (
                      <Button
                        variant="outline"
                        onClick={() => removeFeatureField(index, false)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addFeatureField(false)}
                  className="mt-2"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Feature
                </Button>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="popular-new"
                  checked={newPackage.popular}
                  onChange={(e) => setNewPackage({ ...newPackage, popular: e.target.checked })}
                  className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                />
                <label htmlFor="popular-new" className="text-sm font-medium text-slate-700">
                  Mark as Most Popular
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleAddPackage}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="save-package-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Package
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAdding(false);
                    setNewPackage({
                      name: '',
                      price: '',
                      duration: '',
                      features: [''],
                      popular: false,
                      active: true
                    });
                  }}
                  className="border-slate-300"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Packages */}
      <div className="grid gap-6">
        {packages.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            No packages yet. Click "Add New Package" to create one.
          </div>
        ) : (
          packages.map((pkg) => (
            <Card key={pkg.id} className="border-2" data-testid={`package-card-${pkg.id}`}>
              <CardContent className="pt-6">
                {editingId === pkg.id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Package Name
                        </label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Price (USD)
                        </label>
                        <input
                          type="number"
                          value={editForm.price}
                          onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) || 0 })}
                          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Duration
                      </label>
                      <input
                        type="text"
                        value={editForm.duration}
                        onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Features
                      </label>
                      {editForm.features.map((feature, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => handleFeatureChange(index, e.target.value, true)}
                            className="flex-1 px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                          />
                          {editForm.features.length > 1 && (
                            <Button
                              variant="outline"
                              onClick={() => removeFeatureField(index, true)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        onClick={() => addFeatureField(true)}
                        className="mt-2"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Feature
                      </Button>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`popular-${pkg.id}`}
                        checked={editForm.popular}
                        onChange={(e) => setEditForm({ ...editForm, popular: e.target.checked })}
                        className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                      />
                      <label htmlFor={`popular-${pkg.id}`} className="text-sm font-medium text-slate-700">
                        Mark as Most Popular
                      </label>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        onClick={handleSaveEdit}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleCancelEdit}
                        className="border-slate-300"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <h3 className="text-2xl font-bold text-slate-900">{pkg.name}</h3>
                        {pkg.popular && (
                          <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-1 rounded">
                            Most Popular
                          </span>
                        )}
                        {!pkg.active && (
                          <span className="bg-slate-100 text-slate-600 text-xs font-semibold px-2 py-1 rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="mb-4">
                        <span className="text-3xl font-bold text-emerald-600">${pkg.price}</span>
                        <span className="text-slate-600 ml-2">/ {pkg.duration}</span>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-slate-700">Features:</p>
                        <ul className="space-y-1">
                          {pkg.features.map((feature, index) => (
                            <li key={index} className="text-sm text-slate-600 flex items-start">
                              <span className="text-emerald-600 mr-2">✓</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        onClick={() => handleEdit(pkg)}
                        className="border-slate-300"
                        data-testid={`edit-package-${pkg.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDelete(pkg.id)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`delete-package-${pkg.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
